import { useCallback, useEffect, useRef, useState } from "react";
import {
  hvemErJeg, kanOpprette, mineProduksjoner, nyeRoller, settLaas, slettProduksjon,
  velgProduksjon, type NyRolle, type Produksjonsvalg,
} from "../state/konsoll";
import {
  glemUtkast, hentUtkast, mittNavn, settMittNavn, mittSelskap, settSelskap,
  type MittSelskap, type Utkast,
} from "../state/konsoll";
import { NyProduksjon } from "./konsoll/NyProduksjon";
import * as logo from "../state/logo";
import { lesInvitasjonsfeil, lesNyeInvitasjoner, loggUt }
  from "../state/innlogging";
import { beOmSletting, hentSletteforespoersler, type Sletteforespoersel }
  from "../state/profil";
import { Switch } from "../components/Switch";
import { Merke } from "../components/Merke";

/**
 * Konsollen. Det du møter når du logger inn.
 *
 * ========  SAMME SKALL SOM ARBEIDSFLATA  ========
 *
 * `app`, `skall`, `midt`, `toppstripe`, `kontekst`, `flate` — de samme
 * byggesteinene, i samme rekkefølge. Første utgave var et kort midt på en
 * tom side, og brukeren 30.10.2026: «Dette ligner ikke på arbeidsflatene.»
 * Hen hadde rett i tre ting, og alle tre er strukturelle: det manglet en
 * meny, det manglet en toppstripe, og flata dekket ikke skjermen.
 *
 * ========  DATO DER TIDSKODEN STÅR  ========
 *
 * Toppstripa i arbeidsflata viser tidskoden, fordi den er dagens klokke.
 * Konsollen står ikke i en opptaksdag — det er nettopp det den lar deg
 * velge — så plassen tar datoen i stedet. Samme klasse som flata bruker
 * når man blar til en dag som ikke er i dag: `tidskode--dato`.
 *
 * ========  MENYEN ER ET KRAV, IKKE PYNT  ========
 *
 * Uten en produksjon finnes det ingen profilside, og da fantes det heller
 * ingen vei til å be om sletting av kontoen. Artikkel 17 gjelder fra
 * kontoen finnes, ikke fra man er lagt inn et sted. Derfor har konsollen
 * sin egen meny med KONTO øverst.
 */
export function KonsollScreen({ paaValgt, blackout, onBlackout }: {
  readonly paaValgt: () => void;
  readonly blackout: boolean;
  onBlackout(v: boolean): void;
}): JSX.Element {
  const [liste, setListe] = useState<readonly Produksjonsvalg[] | null>(null);
  const [meg, setMeg] = useState<{ id: string; epost: string } | null>(null);
  const [menyAapen, setMenyAapen] = useState(false);
  const [flate, setFlate] = useState<"liste" | "ny" | "konto">("liste");

  const [utkast, setUtkast] = useState<Utkast | null>(null);
  /* Leses ÉN gang, og nullstilles i samme kall. Et varsel som kom opp
     paa nytt hver gang man gikk innom konsollen ville vaert stoey. */
  const [nye] = useState(() => lesNyeInvitasjoner());
  const [invfeil] = useState(() => lesInvitasjonsfeil());

  /* Fagfunksjoner man har faatt i tillegg siden sist. Settes av samme
     kall som henter lista — se nyeRoller i state/konsoll.ts. */
  const [nyeFag, setNyeFag] = useState<readonly NyRolle[]>([]);

  const les = useCallback((): void => {
    void (async () => {
      const l = await mineProduksjoner();
      setListe(l);
      const nye = nyeRoller(l);
      if (nye.length > 0) setNyeFag(nye);
    })();
    setUtkast(hentUtkast());
  }, []);

  useEffect(() => {
    les();
    void (async () => setMeg(await hvemErJeg()))();
  }, [les]);

  const gaaInn = useCallback((id: string): void => {
    velgProduksjon(id);
    paaValgt();
  }, [paaValgt]);

  const idag = new Date().toLocaleDateString("nb-NO", {
    weekday: "long", day: "numeric", month: "long",
  });

  return (
    <div className="app">
      <KonsollMeny
        aapen={menyAapen}
        epost={meg?.epost ?? ""}
        blackout={blackout}
        onBlackout={onBlackout}
        flate={flate}
        onFlate={(f) => { setFlate(f); setMenyAapen(false); }}
        onLukk={() => setMenyAapen(false)}
      />

      <div className="skall">
        <div className="midt">
          <header className="toppstripe">
            <span className="toppstripe__side">
              <button
                type="button"
                className="menyknapp"
                aria-label={menyAapen ? "Lukk menyen" : "Meny"}
                aria-expanded={menyAapen}
                onClick={() => setMenyAapen(!menyAapen)}
              >
                <span
                  className={`menu-icon${menyAapen ? " menu-icon--open" : ""}`}
                  aria-hidden="true"
                >
                  <span /><span /><span />
                </span>
              </button>
            </span>

            {/* Datoen staar der tidskoden staar paa arbeidsflata. Konsollen
                staar ikke i en opptaksdag — den lar deg velge en. */}
            <span className="tidskode tidskode--dato mono">{idag}</span>

            <span className="toppstripe__side toppstripe__side--hoeyre">
              <span className="toppstripe__hvem">{meg?.epost ?? ""}</span>
            </span>
          </header>

          <p className="kontekst">
            {flate === "konto" ? "Kontoen din"
              : liste === null ? "Henter …"
              : liste.length === 0 ? "Ingen produksjoner ennå"
              : `${liste.length} ${liste.length === 1 ? "produksjon" : "produksjoner"}`}
          </p>
        </div>

        {/* Luften kommer fra `.pager__side` paa arbeidsflata, og konsollen
            gaar ikke gjennom pageren. Uten denne innpakningen staar flata
            rett i kanten til hoeyre, venstre og bunn. */}
        <div className="konsoll__sideflate">
        <div className="flate">
          <div className="flate__innhold">
            {flate === "konto" ? (
              <KontoFlate meg={meg} kanSetteOpp={kanOpprette(liste ?? [])} />
            ) : (
              <div className="rutenett">
                {/* DU BLE LAGT TIL ET STED.
                    `krev_invitasjoner()` binder raden i stillhet ved
                    innlogging — riktig for tilgangen, men den som er
                    lagt inn i en produksjon skal faa vite det. */}
                {invfeil === null ? null : (
                  <p className="konsoll__nyevarsel konsoll__nyevarsel--feil">
                    Invitasjonene dine kunne ikke hentes inn: {invfeil}.
                    Produksjoner du er invitert til vises ikke før dette
                    er rettet.
                  </p>
                )}
                {nyeFag.length === 0 ? null : (
                  <p className="konsoll__nyevarsel">
                    {nyeFag.map((r) => (
                      `Du er nå også ${(ROLLENAVN[r.rolle] ?? r.rolle).toLowerCase()}`
                      + ` i ${r.produksjon}.`
                    )).join(" ")}
                  </p>
                )}
                {nye === 0 ? null : (
                  <p className="konsoll__nyevarsel">
                    Du er lagt til i {nye === 1 ? "en ny produksjon"
                      : `${nye} nye produksjoner`}. {nye === 1 ? "Den"
                      : "De"} står i lista under.
                  </p>
                )}
                {/* UTKAST STAAR FOERST.
                    Et oppsett man ble avbrutt i er det eneste paa denne
                    sida som venter paa noe. Brukeren 30.10.2026: «Legg
                    til en egen kategori som dukker opp om man har noe i
                    kategorien … som er "utkast"». */}
                {utkast === null ? null : (
                  <UtkastKort
                    utkast={utkast}
                    onAapne={() => setFlate("ny")}
                    onForkast={() => { glemUtkast(); setUtkast(null); }}
                  />
                )}
                {(liste ?? []).map((p) => (
                  <ProduksjonKort
                    key={p.produksjonId} p={p}
                    onVelg={() => gaaInn(p.produksjonId)}
                    onEndret={les}
                  />
                ))}
                {/* Bare for den som faar. En koordinator eller en regissoer
                    er lagt inn i noe andre har rigget — og et kort som
                    ville sagt nei er verre enn ingen kort. Basen nekter
                    uansett; se tilgang.kan_opprette(). */}
                {!kanOpprette(liste ?? []) ? null : (
                  <button
                    type="button"
                    className="kort kort--ny"
                    onClick={() => setFlate("ny")}
                  >
                    <span className="kort--ny__pluss" aria-hidden="true">+</span>
                    <span className="kort__tittel">Ny produksjon</span>
                    <span className="kort__status">Du blir med i den med en gang</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Vinduet ligger over alt, og lista blir staaende graanet bak.
          Et oppsett man er midt i skal ikke rive bort det man kom fra. */}
      {flate !== "ny" ? null : (
        <NyProduksjon
          paaFerdig={(id) => gaaInn(id)}
          /* Din egen adresse kan ikke inviteres — du er alt med. Steget
             sier fra paa raden i stedet for aa la basen gjoere det ved
             neste innlogging. Se migrasjon 20261101090000. */
          minEpost={meg?.epost ?? ""}
          /* `les()` ogsaa her: utkastet ble skrevet mens man fylte ut, og
             uten dette staar lista uten kortet til man laster sida om. */
          paaAvbryt={() => { setFlate("liste"); les(); }}
        />
      )}
    </div>
  );
}

/**
 * Menyen på konsollen.
 *
 * Samme form som arbeidsflatas — `meny`, `meny__gruppe`, `meny__rad` — men
 * uten alt som forutsetter en produksjon. Den egne komponenten finnes
 * nettopp fordi `Meny` krever en økt: å gjøre den valgfri der ville lagt
 * null-sjekker i hver eneste rad for å tjene én skjerm.
 */
function KonsollMeny({
  aapen, epost, blackout, onBlackout, flate, onFlate, onLukk,
}: {
  readonly aapen: boolean;
  readonly epost: string;
  readonly blackout: boolean;
  onBlackout(v: boolean): void;
  readonly flate: "liste" | "ny" | "konto";
  onFlate(f: "liste" | "ny" | "konto"): void;
  onLukk(): void;
}): JSX.Element {
  useEffect(() => {
    if (!aapen) return;
    const paaTast = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onLukk();
    };
    window.addEventListener("keydown", paaTast);
    return () => window.removeEventListener("keydown", paaTast);
  }, [aapen, onLukk]);

  return (
    <aside
      className={`meny${aapen ? " meny--aapen" : ""}`}
      aria-label="Meny"
      aria-hidden={!aapen}
    >
      <div className="meny__innhold">
        <Merke sti={null} />

        <div className="meny__gruppe">
          <span className="meny__gruppenavn">SIDE</span>
          <button
            type="button"
            className="meny__rad meny__rad--knapp"
            onClick={() => onFlate("liste")}
          >
            <span>Produksjonene dine</span>
            {flate === "liste" ? <span className="meny__radverdi">✓</span> : null}
          </button>
          <button
            type="button"
            className="meny__rad meny__rad--knapp"
            onClick={() => onFlate("konto")}
          >
            <span>Kontoen din</span>
            {flate === "konto" ? <span className="meny__radverdi">✓</span> : null}
          </button>
        </div>

        <div className="meny__gruppe">
          <span className="meny__gruppenavn">INNSTILLINGER</span>
          <label className="meny__rad">
            <span>Blackout</span>
            <Switch
              value={blackout}
              label="Blackout"
              onChange={() => onBlackout(!blackout)}
            />
          </label>
          {/* Samme begrunnelse som i arbeidsflata: skjermen skal ikke lyse
              opp settet, og konsollen er den foerste flata man moeter naar
              man logger inn midt paa natta. */}
          <span className="meny__merknad">
            Demper flata. Står på til du slår den av, også neste gang.
          </span>
        </div>

        <div className="meny__gruppe">
          <span className="meny__gruppenavn">KONTO</span>
          {/* Raden staar bare naar adressen er lest. En tom rad ser ut
              som en feil, og den foerste tegningen er alltid tom. */}
          {epost === "" ? null : (
            <span className="meny__rad">
              <span className="meny__lesefelt">{epost}</span>
            </span>
          )}
          <button
            type="button"
            className="meny__rad meny__rad--knapp"
            onClick={() => { void loggUt(); }}
          >
            <span>Logg ut</span>
          </button>
          <span className="meny__merknad">
            Du blir værende innlogget i denne nettleseren. Logger du ut, er
            veien inn en ny kode på e-post.
          </span>
        </div>
      </div>
    </aside>
  );
}

/**
 * Kontoen din: selskapet, navnet ditt, og retten etter artikkel 17.
 *
 * ========  HVORFOR SELSKAPET OG NAVNET HØRER HJEMME HER  ========
 *
 * Brukeren 30.10.2026: «Det er inne i "kontoen din" man skal kunne laste
 * opp logoen og navnet til selskapet man jobber i … "Navnet ditt" legger
 * man også til i "kontoen din".» Begge sto i opprettelsesskjemaet, og
 * begge er de samme hver gang man lager en produksjon. Et felt man fyller
 * ut likt hver gang hører ett hakk opp.
 *
 * ========  SLETTERETTEN FØLGER KONTOEN  ========
 *
 * Profilsida inne i en produksjon forutsetter at man ER i en. Den som
 * ikke er medlem noe sted hadde ingen vei til artikkel 17 — og retten
 * gjelder fra kontoen finnes.
 *
 * Den sletter ingenting, og sier det: `production_member.user_id` er
 * `on delete restrict` fordi loggen må kunne vise hvem som skrev en
 * post. Avveiningen tilhører den behandlingsansvarlige.
 */
function KontoFlate({ meg, kanSetteOpp }: {
  readonly meg: { id: string; epost: string } | null;
  /**
   * Selskapet og logoen er prodleders og produsentens.
   *
   * En koordinator eller en A-foto ser dem ikke: basen nekter uansett —
   * `endre on company` spoer om `setter_opp_i_selskap` eller
   * `created_by` — og et felt som ikke lar seg lagre er verre enn
   * ingenting. Navnet ditt og sletteretten gjelder alle.
   */
  readonly kanSetteOpp: boolean;
}): JSX.Element {
  const [navn, setNavn] = useState("");
  const [selskap, setSelskap] = useState<MittSelskap>(
    { id: null, navn: "", logoSti: null });
  const [logourl, setLogourl] = useState<string | null>(null);
  const [lagret, setLagret] = useState<string | null>(null);
  const [feil, setFeil] = useState<string | null>(null);

  const [notat, setNotat] = useState("");
  const [sender, setSender] = useState(false);
  const [tidligere, setTidligere] = useState<readonly Sletteforespoersel[]>([]);

  const les = useCallback((): void => {
    void (async () => {
      setNavn(await mittNavn());
      const sel = await mittSelskap();
      setSelskap(sel);
      setLogourl(await logo.lenke(sel.logoSti));
      setTidligere(await hentSletteforespoersler());
    })();
  }, []);
  useEffect(les, [les]);

  /* Lagres naar feltet forlates, som i oppsettet. Ingen «Lagre»-knapp:
     to felt som hver for seg er ferdige naar man gaar videre. */
  const lagreNavn = useCallback((): void => {
    if (navn.trim() === "") return;
    void (async () => {
      const svar = await settMittNavn(navn);
      setFeil(svar.ok ? null : svar.feil ?? "Ble ikke lagret.");
      if (svar.ok) { setLagret("navn"); window.setTimeout(() => setLagret(null), 1600); }
    })();
  }, [navn]);

  const lagreSelskap = useCallback((): void => {
    if (selskap.navn.trim() === "") return;
    void (async () => {
      const svar = await settSelskap(selskap.navn);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke lagret."); return; }
      setFeil(null);
      setLagret("selskap");
      window.setTimeout(() => setLagret(null), 1600);
      if (selskap.id === null && svar.id !== undefined) {
        setSelskap((s) => ({ ...s, id: svar.id ?? null }));
      }
    })();
  }, [selskap.navn, selskap.id]);

  const velgLogo = useCallback((fil: File | null): void => {
    if (fil === null || selskap.id === null) {
      if (selskap.id === null) setFeil("Skriv navnet på selskapet først.");
      return;
    }
    const galt = logo.sjekkFil(fil);
    if (galt !== null) { setFeil(galt); return; }
    setFeil(null);
    void (async () => {
      const svar = await logo.lastOpp("selskap", selskap.id ?? "", fil, selskap.logoSti);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke lastet opp."); return; }
      les();
    })();
  }, [selskap.id, selskap.logoSti, les]);

  const send = useCallback((): void => {
    if (meg === null || sender) return;
    setSender(true);
    void (async () => {
      const svar = await beOmSletting(meg.id, notat);
      setSender(false);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke sendt."); return; }
      setNotat("");
      les();
    })();
  }, [meg, notat, sender, les]);

  return (
    <div className="rutenett">
      {!kanSetteOpp ? null : (
      <div className="loggkort konsoll__kort--bredt">
        <div className="loggkort__hode">
          <span className="loggkort__navn">Selskapet ditt</span>
          {lagret === "selskap"
            ? <span className="loggkort__lag">Lagret</span> : null}
        </div>
        <div className="konsoll__felter">
          <label className="oppstart__merke">
            Navn
            <input
              className="konsoll__felt" value={selskap.navn}
              placeholder="Tokt film og tv" autoComplete="organization"
              onChange={(e) => setSelskap((s) => ({ ...s, navn: e.target.value }))}
              onBlur={lagreSelskap}
            />
          </label>

          <span className="oppstart__merke">Logo</span>
          <div className="logo__rad">
            <div className="logo__rute">
              {logourl === null
                ? <span className="logo__tom" aria-hidden="true" />
                : <img className="logo__bilde" src={logourl} alt="" />}
            </div>
            <div className="logo__tekst">
              <span className="merknad">
                Står i appen og på nettflata for alle produksjonene dine.
                En produksjon med egen logo bruker sin.
              </span>
              <span className="merknad">
                PNG uten bakgrunn anbefales.
              </span>
            </div>
            <div className="logo__knapper">
              <label className="synk__knapp logo__knapp">
                {selskap.logoSti === null ? "Last opp" : "Bytt"}
                <input
                  className="logo__velger" type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={(e) => {
                    velgLogo(e.target.files?.[0] ?? null); e.target.value = "";
                  }}
                />
              </label>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="loggkort konsoll__kort--bredt">
        <div className="loggkort__hode">
          <span className="loggkort__navn">Deg</span>
          {lagret === "navn" ? <span className="loggkort__lag">Lagret</span> : null}
        </div>
        <div className="konsoll__felter">
          <label className="oppstart__merke">
            Navnet ditt
            <input
              className="konsoll__felt" value={navn} autoComplete="name"
              onChange={(e) => setNavn(e.target.value)}
              onBlur={lagreNavn}
            />
          </label>
          <p className="merknad konsoll__merknad">
            Dette er navnet crewet ser på deg i produksjonene du er med i.
          </p>
          <span className="oppstart__merke">E-post</span>
          <span className="meny__lesefelt">{meg?.epost ?? "—"}</span>
          <p className="merknad konsoll__merknad">
            Adressen er det eneste vi lagrer om deg utenfor en produksjon.
          </p>
        </div>
      </div>

      <div className="loggkort konsoll__kort--bredt">
        <div className="loggkort__hode">
          <span className="loggkort__navn">Be om sletting</span>
        </div>
        <div className="konsoll__felter">
          <p className="merknad konsoll__merknad">
            Dette sletter ingenting av seg selv. Loggen må kunne vise hvem
            som skrev en post, og avveiningen mellom sletteretten din og
            integriteten i loggen tilhører produksjonsselskapet.
            Forespørselen får en dato og en eier, og du får svar.
          </p>
          <label className="oppstart__merke">
            Hva gjelder det?
            <textarea
              className="konsoll__felt konsoll__felt--tekst" rows={3}
              value={notat} placeholder="Valgfritt"
              onChange={(e) => setNotat(e.target.value)}
            />
          </label>
          <button
            type="button" className="synk__knapp"
            disabled={sender || meg === null}
            onClick={send}
          >
            {sender ? "Sender …" : "Send forespørsel"}
          </button>

          {tidligere.length === 0 ? null : (
            <>
              <span className="oppstart__merke">Sendt tidligere</span>
              {tidligere.map((f) => (
                <span key={f.id} className="meny__rad">
                  <span className="meny__lesefelt">
                    {new Date(f.dato).toLocaleDateString("nb-NO")}
                    {f.notat === "" ? "" : ` · ${f.notat}`}
                  </span>
                  <span className="meny__radverdi">
                    {f.status === "open" ? "Venter"
                      : f.status === "done" ? "Utført" : "Avslått"}
                  </span>
                </span>
              ))}
            </>
          )}
        </div>
      </div>

      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}

/**
 * Et oppsett som ikke ble ferdig.
 *
 * Ligger i nettleseren, ikke i basen: et halvferdig oppsett er ikke en
 * produksjon, og en paabegynt rad i `production` ville staatt i lista for
 * alle som ser selskapet. Se `state/konsoll.ts`.
 */
function UtkastKort({ utkast, onAapne, onForkast }: {
  readonly utkast: Utkast;
  readonly onAapne: () => void;
  readonly onForkast: () => void;
}): JSX.Element {
  const naar = new Date(utkast.lagret).toLocaleString("nb-NO", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className="kort kort--produksjon kort--utkast">
      <button type="button" className="konsoll__inn" onClick={onAapne}>
        <span className="konsoll__utkastmerke">Utkast</span>
        <span className="kort__tittel">
          {utkast.navn.trim() === "" ? "Uten navn ennå" : utkast.navn}
        </span>
        <span className="kort__status">
          {utkast.dager.length === 0 ? "Ingen dager valgt"
            : `${utkast.dager.length} opptaksdager`}
        </span>
        <span className="kort__status">Sist endret {naar}</span>
      </button>
      <div className="konsoll__eierrad">
        <button type="button" className="synk__knapp konsoll__eierknapp"
                onClick={onAapne}>
          Fortsett
        </button>
        <button type="button" className="synk__knapp konsoll__eierknapp"
                onClick={onForkast}>
          Forkast
        </button>
      </div>
    </div>
  );
}

const ROLLENAVN: Readonly<Record<string, string>> = {
  prodleder: "Produksjonsleder", produsent: "Produsent", redaktor: "Redaktør",
  regi: "Regi", vj: "VJ", klipper: "Klipper", crew: "Crew",
  reporter: "Reporter", tekniker: "Tekniker", afoto: "A-foto",
  koordinator: "Koordinator",
};

/**
 * Ett kort per produksjon — og eierens kontroll over den.
 *
 * ========  KONTROLLEN LIGGER HER, IKKE I ARBEIDSFLATA  ========
 *
 * Brukeren 30.10.2026: «I selve arbeidsflaten skal man ikke kunne slette
 * dokumenter osv, men i konsollen skal man ha kontroll på alt.» Det er en
 * riktig deling: arbeidsflata er der man jobber en opptaksdag, og ingen
 * skal kunne rive bort grunnlaget mens noen står på sett. Konsollen er
 * der man forvalter.
 *
 * ========  LÅS FØR SLETT  ========
 *
 * «Lås» står først fordi det er det riktige svaret ni av ti ganger. En
 * ferdig produksjon skal som regel bli stående å leses — den slettes når
 * noen har bestemt at den skal bort, ikke når den er over.
 *
 * Slettingen er to trykk, som fjerningen av et dokument: første armerer,
 * andre utfører, og armeringen faller av etter fire sekunder. Den kan
 * ikke angres, og det står på knappen.
 */
function ProduksjonKort({ p, onVelg, onEndret }: {
  readonly p: Produksjonsvalg;
  readonly onVelg: () => void;
  readonly onEndret: () => void;
}): JSX.Element {
  const [jobber, setJobber] = useState(false);
  const [armert, setArmert] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  const ur = useRef<number | null>(null);

  useEffect(() => () => {
    if (ur.current !== null) window.clearTimeout(ur.current);
  }, []);

  /* Sluttdatoen staar fordi den betyr noe: den styrer naar loggen slettes
     fra crewets telefoner. En produksjon som er over skal kjennes paa
     kortet, ikke oppdages inne i den. */
  const slutt = new Date(`${p.sluttdatoIso}T00:00:00`);
  const over = slutt.getTime() < Date.now();

  const laas = useCallback((): void => {
    setJobber(true);
    setFeil(null);
    void (async () => {
      const svar = await settLaas(p.produksjonId, !p.laast);
      setJobber(false);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke endret."); return; }
      onEndret();
    })();
  }, [p.produksjonId, p.laast, onEndret]);

  const slett = useCallback((): void => {
    if (ur.current !== null) window.clearTimeout(ur.current);
    if (!armert) {
      setArmert(true);
      ur.current = window.setTimeout(() => setArmert(false), 4000);
      return;
    }
    setArmert(false);
    setJobber(true);
    setFeil(null);
    void (async () => {
      const svar = await slettProduksjon(p.produksjonId);
      setJobber(false);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke slettet."); return; }
      onEndret();
    })();
  }, [armert, p.produksjonId, onEndret]);

  return (
    <div className="kort kort--produksjon">
      {/* Hele kortet er veien inn, bortsett fra knappene nederst. En `div`
          rundt en `button` og ikke en knapp i en knapp — det siste er
          hverken gyldig oppmerking eller noe man faar til aa klikke. */}
      <button type="button" className="konsoll__inn" onClick={onVelg}>
        <span className="konsoll__logo"><Merke sti={p.logoSti} /></span>
        <span className="kort__tittel">{p.navn}</span>
        <span className="kort__status">
          {/* Har man flere roller i produksjonen, staar de sammen paa
              samme kort. Brukeren 01.11.2026: «ikke en boks per rolle». */}
          {p.selskap ?? "Uten selskap"} ·{" "}
          {p.roller.map((r) => ROLLENAVN[r] ?? r).join(" og ")}
        </span>
        <span className={`kort__status${over ? " kort__status--feil" : ""}`}>
          {over ? "Avsluttet " : "Til og med "}
          {slutt.toLocaleDateString("nb-NO",
            { day: "numeric", month: "short", year: "numeric" })}
        </span>
        {!p.laast ? null : (
          <span className="konsoll__laast">Låst · ingen kan skrive i den</span>
        )}
      </button>

      {!p.eier ? null : (
        <div className="konsoll__eierrad">
          <button
            type="button"
            className="synk__knapp konsoll__eierknapp"
            disabled={jobber}
            onClick={laas}
          >
            {p.laast ? "Lås opp" : "Lås"}
          </button>
          <button
            type="button"
            className={`synk__knapp konsoll__eierknapp${armert ? " konsoll__armert" : ""}`}
            disabled={jobber}
            onClick={slett}
          >
            {jobber ? "…" : armert ? "Slett for godt?" : "Slett"}
          </button>
        </div>
      )}
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}
